import React, {useState} from "react";

import {LoadingView} from "../../../../designSystem/components/LoadingView";
import {SearchBar} from "../../../../designSystem/components/SearchBar";
import {ActionButton} from "../../../../designSystem/components/ActionButton";
import VaccineButtons from "../../../../designSystem/components/VaccineButtons/VaccineButtons";
import {Dialog} from "../../../../designSystem/components/Dialog";
import {StandardLayout} from "../../../../components/StandardLayout";
import { PaginationBar, TotalDisplay } from "../../../../components/PaginationBar";
import MedicalOrganizationTable from "../MedicalOrganizationTable/MedicalOrganizationTable";

import { MedicalOrganizationCategoryButtons } from "../MedicalOrganizationCategoryButtons";
import {MedicalOrganizationTags} from "../MedicalOrganizationTags/MedicalOrganizationTags";
import {MEDICAL_ORGANIZATION_MODAL_CONTENT, MedicalOrganizationModalContent} from "../MedicalOrganizationModalContent";

import {MedicalOrganizationVaccine} from "../../../../types/MedicalOrganizationVaccine";
import {DEFAULT_MEDICAL_ORGANIZATION} from "../../model/defaultMedicalOrganization";

import {useExportMedicalOrganizationManagement} from "../../hooks/useExportMedicalOrganizationManagement";
import {useMedicalOrganizationMutations} from "../../hooks/useMedicalOrganizationMutations";
import {useMedicalOrganizationVaccines} from "../../hooks/useMedicalOrganizationVaccines";
import {useMedicalOrganizationFilters} from "../../hooks/useMedicalOrganizationFilters";
import {useMedicalOrganizationListView} from "../../hooks/useMedicalOrganizationListView";

const PAGE_SIZE = 30;

export const MedicalOrganizationManagementFeature = ({ userToken }) => {
  const [sortColumn, setSortColumn] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [dialogContent, setDialogContent] = useState<MEDICAL_ORGANIZATION_MODAL_CONTENT | ''>('');
  const [formMode, setFormMode] = useState<'edit' | 'add'>('edit');
  const [editingMedicalOrganizationVaccine, setEditingMedicalOrganizationVaccine] = useState<MedicalOrganizationVaccine>();
  const {
    loading,
    medicalOrganizationVaccines,
    fetchMedicalOrganizationVaccines,
  } = useMedicalOrganizationVaccines();
  const {
    searchTerm,
    setSearchTerm,
    selectedCategories,
    selectedVaccine,
    tagFormData,
    currentPage,
    setCurrentPage,
    onChangeCategoriesSelector,
    onChangeSelectedVaccine,
    onChangeTagFormData,
  } = useMedicalOrganizationFilters();

  const handleSort: (column: string) => void = (column) => {
    const newOrder = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(newOrder);
  };

  // trigger when the dialog backdrop is clicked, to close the dialog
  const handleDialogBackdropClick = () => {
    setDialogContent('')
  }

  // trigger when click on the "新增醫療機構" button in the top right corner
  const handleAdd = () => {
    setEditingMedicalOrganizationVaccine(DEFAULT_MEDICAL_ORGANIZATION);
    setFormMode('add')
    setDialogContent(MEDICAL_ORGANIZATION_MODAL_CONTENT.FORM)
  }

  // handle click on the "修改資料" button at the 動作 column
  const handleEdit = (medicalOrganizationVaccine: MedicalOrganizationVaccine) => {
    setEditingMedicalOrganizationVaccine(medicalOrganizationVaccine);
    setFormMode('edit')
    setDialogContent(MEDICAL_ORGANIZATION_MODAL_CONTENT.FORM)
  }

  // handle click on the "刪除" button at the 動作 column
  const handleDelete = (medicalOrganizationVaccine: MedicalOrganizationVaccine) => {
    setEditingMedicalOrganizationVaccine(medicalOrganizationVaccine);
    setDialogContent(MEDICAL_ORGANIZATION_MODAL_CONTENT.DELETE)
  }

  // trigger when the editingMedicalOrganization in the form changed
  const onChangeEditingMedicalOrganization = (medicalOrganizationVaccine: MedicalOrganizationVaccine) => {
    setEditingMedicalOrganizationVaccine(medicalOrganizationVaccine)
  }

  const {onClickSaveEdit, onClickConfirmDelete} = useMedicalOrganizationMutations({
    formMode,
    editingMedicalOrganizationVaccine,
    userToken,
    setDialogContent,
    fetchMedicalOrganizationVaccines,
  });

  const {
    filteredMedicalOrganizationVaccines,
    sortedMedicalOrganizationVaccines,
    numberOfPages,
    paginatedMedicalOrganizationVaccines,
  } = useMedicalOrganizationListView({
    medicalOrganizationVaccines,
    selectedCategories,
    selectedVaccine,
    tagFormData,
    searchTerm,
    sortColumn,
    sortOrder,
    currentPage,
    pageSize: PAGE_SIZE,
  });

  const { exportData } = useExportMedicalOrganizationManagement({ data: sortedMedicalOrganizationVaccines})

  if (loading)
    return <LoadingView />

  return (
    <StandardLayout
      headerTitle="就醫資訊管理"
      headerActionLabel="新增醫療機構"
      headerActionOnClick={handleAdd}
      body={
        <div className="w-[900px] m-auto flex flex-col p-8 gap-4">
          <SearchBar
            placeholder="搜尋醫療機構名稱/地址"
            value={searchTerm}
            onChangeValue={(value: string) => setSearchTerm(value)}
          />

          <div className="flex flex-col">
            <MedicalOrganizationCategoryButtons
              selected={selectedCategories}
              onChangeSelected={onChangeCategoriesSelector}
            />
            <VaccineButtons
              selected={selectedVaccine}
              onChangeSelected={onChangeSelectedVaccine}
            />
            <MedicalOrganizationTags
              formData={tagFormData}
              onChangeFormData={onChangeTagFormData}
            />
          </div>

          <div className="flex flex-row justify-between items-center">
            <TotalDisplay numberOfData={filteredMedicalOrganizationVaccines.length}/>
            <ActionButton
              label="匯出資料"
              onClick={exportData}
              iconName="fa-file-export"
              variant="outline"
            />
          </div>

          {filteredMedicalOrganizationVaccines.length > 0 && (
            <>
              <MedicalOrganizationTable
                data={paginatedMedicalOrganizationVaccines}
                sortOrder={sortOrder}
                sortColumn={sortColumn}
                handleSort={handleSort}
                handleDelete={handleDelete}
                handleEdit={handleEdit}
              />
              <PaginationBar
                currentPage={currentPage}
                numberOfPages={numberOfPages}
                onPageChange={(nextPage: number) => setCurrentPage(nextPage)}
              />
            </>
          )}
          <Dialog
            isOpen={dialogContent !== ''}
            onBackdropClick={handleDialogBackdropClick}
          >
            {MedicalOrganizationModalContent({
              formMode,
              editingMedicalOrganizationVaccine,
              onChangeEditingMedicalOrganization,
              onClickSaveEdit,
              onClickDontDelete: handleDialogBackdropClick,
              onClickConfirmDelete,
            })[dialogContent] || <></>}
          </Dialog>
        </div>
      }
    />
  )
}
